<!-- components/dashboard/cards/EquipeTrabalhandoCard.vue -->
<template>
    <div class="equipe-trabalhando-card">
      <div class="card-header">
        <h3>👥 Equipe Trabalhando</h3>
        <div class="status-geral">
          <span v-if="funcionarios.length > 0" class="status-ativo">
            {{ funcionarios.length }} pessoa{{ funcionarios.length > 1 ? 's' : '' }} trabalhando
          </span>
          <span v-else class="status-inativo">
            Nenhum funcionário trabalhando no momento
          </span>
        </div>
      </div>
  
      <!-- Loading -->
      <div v-if="carregando" class="loading-equipe">
        🔄 Carregando equipe...
      </div>
  
      <!-- Lista de Funcionários -->
      <div v-else-if="funcionarios.length > 0" class="funcionarios-lista">
        <div 
          v-for="funcionario in funcionarios" 
          :key="funcionario.id"
          class="funcionario-item"
        >
          <div class="funcionario-avatar">
            <span class="avatar-icon">👤</span>
            <div class="status-indicator" :class="funcionario.status"></div>
          </div>
          
          <div class="funcionario-info">
            <div class="funcionario-nome">
              <strong>{{ funcionario.nome }}</strong>
            </div>
            <div class="funcionario-detalhes">
              <span class="setor">🏭 {{ funcionario.setor }}</span>
              <span class="inicio">🕐 Desde {{ formatarHora(funcionario.inicio_trabalho) }}</span>
            </div>
            <div class="funcionario-tempo">
              <span class="tempo-trabalhado">
                ⏱️ {{ formatarTempoTrabalhado(funcionario.tempo_trabalhado_minutos) }}
              </span>
              <span class="producao" v-if="funcionario.producao_hoje">
                📊 {{ funcionario.producao_hoje }} peças hoje
              </span>
            </div>
          </div>
          
          <div class="funcionario-actions">
            <button @click="verDetalhesFuncionario(funcionario)" class="btn-detalhes">
              👁️ Ver Detalhes
            </button>
          </div>
        </div>
      </div>
  
      <!-- Estado Vazio -->
      <div v-else class="estado-vazio">
        <div class="vazio-icon">😴</div>
        <h4>Nenhum funcionário trabalhando</h4>
        <p>Esta ordem não possui funcionários ativos no momento.</p>
        <div class="sugestoes">
          <p><strong>Possíveis motivos:</strong></p>
          <ul>
            <li>🕐 Fora do horário de expediente</li>
            <li>📋 Ordem ainda não iniciada</li>
            <li>✅ Ordem já finalizada</li>
            <li>🔄 Aguardando próxima etapa</li>
          </ul>
        </div>
      </div>
  
      <!-- Rodapé com Ações -->
      <div v-if="funcionarios.length > 0" class="card-footer">
        <div class="resumo-tempo">
          <span class="tempo-total">
            ⏱️ Tempo total: {{ calcularTempoTotal() }}
          </span>
          <span class="media-tempo">
            📊 Média: {{ calcularTempoMedio() }} por pessoa
          </span>
        </div>
        <div class="footer-actions">
          <button @click="atualizarEquipe" class="btn-atualizar">
            🔄 Atualizar
          </button>
          <button @click="verEquipeCompleta" class="btn-equipe-completa">
            👥 Ver Equipe Completa
          </button>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { producaoService } from '@/services/producaoService';
  
  export default {
    name: 'EquipeTrabalhandoCard',
    
    props: {
      ordem: {
        type: Object,
        required: true
      }
    },
    
    emits: ['ver-funcionario', 'ver-equipe-completa'],
    
    data() {
      return {
        funcionarios: [],
        carregando: false,
        ultimaAtualizacao: null
      };
    },
    
    mounted() {
      this.carregarEquipe();
      
      // Atualizar a cada 30 segundos
      this.intervalId = setInterval(() => {
        this.carregarEquipe();
      }, 30000);
    },
    
    beforeUnmount() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
    },
    
    watch: {
      ordem: {
        handler() {
          this.carregarEquipe();
        },
        deep: true
      }
    },
    
    methods: {
      async carregarEquipe() {
        if (!this.ordem?.id) return;
        
        try {
          this.carregando = true;
          console.log('👥 Carregando equipe da ordem:', this.ordem.id);
          
          // Buscar funcionários trabalhando na ordem
          const response = await this.buscarFuncionariosOrdem(this.ordem.id);
          
          if (response.status === 'sucesso') {
            this.funcionarios = response.funcionarios || [];
            this.ultimaAtualizacao = new Date();
            console.log('✅ Equipe carregada:', this.funcionarios.length, 'funcionários');
          } else {
            this.funcionarios = [];
          }
          
        } catch (error) {
          console.error('❌ Erro ao carregar equipe:', error);
          this.funcionarios = [];
        } finally {
          this.carregando = false;
        }
      },
      
      async buscarFuncionariosOrdem(idOrdem) {
        try {
          // Tentar buscar funcionários específicos da ordem
          const url = `/dashboard-producao/ordem/${idOrdem}/funcionarios-trabalhando`;
          const response = await producaoService.api.get(url);
          return response.data;
        } catch (error) {
          console.log('ℹ️ API específica não disponível, usando dados simulados');
          return this.gerarDadosSimulados();
        }
      },
      
      gerarDadosSimulados() {
        // Dados simulados baseados na ordem
        const funcionariosSimulados = [];
        
        if (this.ordem.status === 'em_andamento') {
          // Simular funcionários trabalhando
          const nomes = ['João Silva', 'Maria Santos', 'Pedro Oliveira', 'Ana Costa'];
          const setores = ['CORTE', 'COSTURA', 'ACABAMENTO', 'REVISÃO'];
          
          const numFuncionarios = Math.floor(Math.random() * 3) + 1; // 1 a 3 funcionários
          
          for (let i = 0; i < numFuncionarios; i++) {
            funcionariosSimulados.push({
              id: i + 1,
              nome: nomes[i],
              setor: setores[i] || 'PRODUÇÃO',
              status: 'trabalhando',
              inicio_trabalho: new Date(Date.now() - Math.random() * 4 * 60 * 60 * 1000), // Até 4h atrás
              tempo_trabalhado_minutos: Math.floor(Math.random() * 240) + 60, // 1h a 4h
              producao_hoje: Math.floor(Math.random() * 50) + 10 // 10 a 60 peças
            });
          }
        }
        
        return {
          status: 'sucesso',
          funcionarios: funcionariosSimulados
        };
      },
      
      formatarHora(dataHora) {
        if (!dataHora) return 'N/A';
        try {
          return new Date(dataHora).toLocaleTimeString('pt-BR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
        } catch (error) {
          return 'N/A';
        }
      },
      
      formatarTempoTrabalhado(minutos) {
        if (!minutos) return '0min';
        
        const horas = Math.floor(minutos / 60);
        const mins = minutos % 60;
        
        if (horas > 0) {
          return `${horas}h ${mins}min`;
        } else {
          return `${mins}min`;
        }
      },
      
      calcularTempoTotal() {
        const totalMinutos = this.funcionarios.reduce((total, func) => {
          return total + (func.tempo_trabalhado_minutos || 0);
        }, 0);
        
        return this.formatarTempoTrabalhado(totalMinutos);
      },
      
      calcularTempoMedio() {
        if (this.funcionarios.length === 0) return '0min';
        
        const totalMinutos = this.funcionarios.reduce((total, func) => {
          return total + (func.tempo_trabalhado_minutos || 0);
        }, 0);
        
        const media = Math.round(totalMinutos / this.funcionarios.length);
        return this.formatarTempoTrabalhado(media);
      },
      
      verDetalhesFuncionario(funcionario) {
        this.$emit('ver-funcionario', funcionario);
        console.log('👤 Ver detalhes do funcionário:', funcionario.nome);
      },
      
      verEquipeCompleta() {
        this.$emit('ver-equipe-completa', this.ordem);
        console.log('👥 Ver equipe completa da ordem:', this.ordem.id);
      },
      
      atualizarEquipe() {
        this.carregarEquipe();
      }
    }
  }
  </script>
  
  <style scoped>
  .equipe-trabalhando-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    margin-bottom: 20px;
  }
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #ecf0f1;
  }
  
  .card-header h3 {
    margin: 0;
    color: #2c3e50;
  }
  
  .status-geral {
    font-size: 14px;
  }
  
  .status-ativo {
    color: #27ae60;
    font-weight: 500;
  }
  
  .status-inativo {
    color: #7f8c8d;
    font-style: italic;
  }
  
  .loading-equipe {
    text-align: center;
    padding: 40px;
    color: #7f8c8d;
  }
  
  .funcionarios-lista {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
  
  .funcionario-item {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #27ae60;
    transition: all 0.3s ease;
  }
  
  .funcionario-item:hover {
    background: #e8f5e8;
    transform: translateX(5px);
  }
  
  .funcionario-avatar {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: #3498db;
    border-radius: 50%;
    color: white;
    font-size: 20px;
  }
  
  .status-indicator {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid white;
  }
  
  .status-indicator.trabalhando {
    background: #27ae60;
  }
  
  .funcionario-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  
  .funcionario-nome strong {
    color: #2c3e50;
    font-size: 16px;
  }
  
  .funcionario-detalhes {
    display: flex;
    gap: 15px;
    font-size: 13px;
    color: #7f8c8d;
  }
  
  .funcionario-tempo {
    display: flex;
    gap: 15px;
    font-size: 12px;
    color: #34495e;
  }
  
  .btn-detalhes {
    padding: 8px 12px;
    background: #3498db;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.3s ease;
  }
  
  .btn-detalhes:hover {
    background: #2980b9;
    transform: translateY(-2px);
  }
  
  .estado-vazio {
    text-align: center;
    padding: 40px 20px;
    color: #7f8c8d;
  }
  
  .vazio-icon {
    font-size: 48px;
    margin-bottom: 15px;
  }
  
  .estado-vazio h4 {
    margin: 0 0 10px 0;
    color: #2c3e50;
  }
  
  .estado-vazio p {
    margin: 0 0 20px 0;
  }
  
  .sugestoes {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
    text-align: left;
    max-width: 300px;
    margin: 0 auto;
  }
  
  .sugestoes ul {
    margin: 10px 0 0 0;
    padding-left: 20px;
  }
  
  .sugestoes li {
    margin: 5px 0;
    font-size: 14px;
  }
  
  .card-footer {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #ecf0f1;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .resumo-tempo {
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 13px;
    color: #7f8c8d;
  }
  
  .footer-actions {
    display: flex;
    gap: 10px;
  }
  
  .btn-atualizar,
  .btn-equipe-completa {
    padding: 8px 12px;
    border: 1px solid #ddd;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.3s ease;
  }
  
  .btn-atualizar:hover {
    background: #e3f2fd;
    border-color: #3498db;
  }
  
  .btn-equipe-completa:hover {
    background: #e8f5e8;
    border-color: #27ae60;
  }
  
  /* Responsividade */
  @media (max-width: 768px) {
    .funcionario-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
    
    .funcionario-detalhes,
    .funcionario-tempo {
      flex-direction: column;
      gap: 5px;
    }
    
    .card-footer {
      flex-direction: column;
      gap: 15px;
      align-items: flex-start;
    }
    
    .footer-actions {
      align-self: stretch;
    }
    
    .btn-atualizar,
    .btn-equipe-completa {
      flex: 1;
    }
  }
  </style>